export const ERROR_RECORDS_UPDATED_EVENT = 'error_records:updated';

export const SESSION_SAFE_UPDATE = 'SESSION_SAFE_UPDATE';
export const SESSION_INVALIDATING = 'SESSION_INVALIDATING';

export const IMPLICIT_UPDATE_AUDIT_FIELDS = Object.freeze([
  'updated_at',
  'updated_by_user',
  'updated_by_user_id',
]);

const MEMBERSHIP_FIELDS = new Set(['type', 'id']);
const ALWAYS_INVALIDATING_FIELDS = new Set(['files', 'type']);

const SCALAR_FIELDS = [
  'type',
  'specific_id',
  'article_number',
  'article_name',
  'order_number',
  'customer_id',
  'offender_id',
  'repair_costs',
  'total_amount',
  'defective_pieces',
  'group_id',
  'ack_error',
  'claim_counterparty_number',
  'error_type_id',
  'contractor_id',
  'contractor_note',
  'record_processor_id',
  'problem_description',
  'main_reason',
  'immediate_measure',
  'long_term_measure',
  'prevention_id',
  'root_cause_id',
  'auditor_id',
  'auditor_note',
  'state',
];

const FIELD_ALIASES = {
  type: ['type'],
  specific_id: ['specific_id'],
  article_number: ['article_number'],
  article_name: ['article_name'],
  order_number: ['order_number'],
  customer_id: ['customer_id', 'customer.name'],
  'customer.name': ['customer_id', 'customer.name'],
  offender_id: ['offender_id'],
  repair_costs: ['repair_costs'],
  total_amount: ['total_amount'],
  defective_pieces: ['defective_pieces'],
  group_id: ['group_id', 'group.name'],
  'group.name': ['group_id', 'group.name'],
  ack_error: ['ack_error'],
  claim_counterparty_number: ['claim_counterparty_number'],
  error_type_id: ['error_type_id', 'error_type.name'],
  'error_type.name': ['error_type_id', 'error_type.name'],
  contractor_id: ['contractor_id', 'contractor.name'],
  'contractor.name': ['contractor_id', 'contractor.name'],
  contractor_note: ['contractor_note'],
  record_processor_id: ['record_processor_id', 'record_processor.name'],
  'record_processor.name': ['record_processor_id', 'record_processor.name'],
  problem_description: ['problem_description'],
  main_reason: ['main_reason'],
  immediate_measure: ['immediate_measure'],
  long_term_measure: ['long_term_measure'],
  prevention_id: ['prevention_id', 'prevention.name'],
  'prevention.name': ['prevention_id', 'prevention.name'],
  root_cause_id: ['root_cause_id', 'root_cause.name'],
  'root_cause.name': ['root_cause_id', 'root_cause.name'],
  auditor_id: ['auditor_id', 'auditor.name'],
  'auditor.name': ['auditor_id', 'auditor.name'],
  auditor_note: ['auditor_note'],
  state: ['state'],
  id: ['id'],
  updated_at: ['updated_at'],
  updated_by_user: ['updated_at'],
  updated_by_user_id: ['updated_at'],
  created_at: ['created_at'],
  created_by_user: ['created_at'],
  created_by_user_id: ['created_at'],
};

export const ERROR_RECORD_WRITE_FIELDS = Object.freeze([
  'type',
  'article_number',
  'article_name',
  'order_number',
  'customer_id',
  'offender_id',
  'repair_costs',
  'total_amount',
  'defective_pieces',
  'group_id',
  'ack_error',
  'claim_counterparty_number',
  'error_type_id',
  'contractor_note',
  'record_processor_id',
  'problem_description',
  'main_reason',
  'immediate_measure',
  'long_term_measure',
  'prevention_id',
  'root_cause_id',
  'auditor_id',
  'auditor_note',
  'state',
]);

function unwrapErrorRecord(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data) && payload.data.id != null) {
    return payload.data;
  }
  return payload;
}

function scalar(value) {
  if (value == null) return '';
  if (typeof value === 'object') {
    if (value.id != null) return String(value.id);
    return '';
  }
  return String(value);
}

function expandFields(fields) {
  const expanded = new Set();
  (Array.isArray(fields) ? fields : []).forEach((field) => {
    if (field == null || field === '') return;
    const aliases = FIELD_ALIASES[field] || [field];
    aliases.forEach((alias) => expanded.add(alias));
  });
  return expanded;
}

function hasActiveValue(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.some((item) => hasActiveValue(item));
  return String(value).trim() !== '';
}

function isDefaultSpecificIdDescSorter(sorter) {
  const field = sorter?.field || sorter?.column;
  const dir = sorter?.dir || 'asc';
  return field === 'specific_id' && dir === 'desc';
}

export function isTableCompleteErrorRecordRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
  if (row.id == null || row.id === '') return false;
  return typeof row.type === 'string' && row.type !== '';
}

export function collectErrorRecordChangedFields(previous, next, options = {}) {
  const prev = previous && typeof previous === 'object' ? previous : {};
  const nxt = next && typeof next === 'object' ? next : {};
  const changed = [];
  SCALAR_FIELDS.forEach((key) => {
    if (scalar(prev[key]) !== scalar(nxt[key])) changed.push(key);
  });
  if (prev.id != null && nxt.id != null && String(prev.id) !== String(nxt.id)) changed.push('id');
  if (options.implicitAudit) changed.push(...IMPLICIT_UPDATE_AUDIT_FIELDS);
  if (options.hasFiles) changed.push('files');
  return Array.from(new Set(changed));
}

export function classifyErrorRecordListMutation(event, session = {}) {
  const detail = event?.detail;
  if (!detail || typeof detail !== 'object') return SESSION_INVALIDATING;
  if (detail.operation !== 'update') return SESSION_INVALIDATING;
  if (!Array.isArray(detail.changedFields) || detail.changedFields.length === 0) return SESSION_INVALIDATING;
  const row = unwrapErrorRecord(detail.row);
  if (!isTableCompleteErrorRecordRow(row)) return SESSION_INVALIDATING;
  const id = detail.id ?? row.id;
  if (id == null || id === '') return SESSION_INVALIDATING;
  if (typeof session.isRowLoaded !== 'function' || !session.isRowLoaded(id)) return SESSION_INVALIDATING;
  if (String(session.toolbarSearch ?? '').trim() !== '') return SESSION_INVALIDATING;

  const changedFields = Array.from(new Set([
    ...detail.changedFields,
    ...IMPLICIT_UPDATE_AUDIT_FIELDS,
  ]));
  const expanded = expandFields(changedFields);
  for (const field of expanded) {
    if (MEMBERSHIP_FIELDS.has(field) || ALWAYS_INVALIDATING_FIELDS.has(field)) return SESSION_INVALIDATING;
  }
  if (changedFields.some((field) => ALWAYS_INVALIDATING_FIELDS.has(field))) return SESSION_INVALIDATING;

  const filters = Array.isArray(session.activeHeaderFilters) ? session.activeHeaderFilters : [];
  for (const filter of filters) {
    if (!filter?.field || !hasActiveValue(filter.value)) continue;
    const aliases = expandFields([filter.field]);
    for (const alias of aliases) {
      if (expanded.has(alias)) return SESSION_INVALIDATING;
    }
  }

  const sorters = Array.isArray(session.activeSorters) ? session.activeSorters : [];
  for (const sorter of sorters) {
    const field = sorter?.field || sorter?.column;
    if (!field) continue;
    if (isDefaultSpecificIdDescSorter(sorter) && !expanded.has('specific_id')) continue;
    const aliases = expandFields([field]);
    for (const alias of aliases) {
      if (expanded.has(alias)) return SESSION_INVALIDATING;
    }
  }

  return SESSION_SAFE_UPDATE;
}

export function applyPagedMutationOverlay(response, overlay) {
  if (!response || typeof response !== 'object' || !Array.isArray(response.data)) return response;
  if (!overlay || typeof overlay.get !== 'function' || overlay.size === 0) return response;
  return {
    ...response,
    data: response.data.map((row) => {
      if (!row || row.id == null) return row;
      return overlay.get(String(row.id)) || row;
    }),
  };
}

export function normalizeErrorRecordTimestamp(value) {
  if (value == null || value === '') return '';
  const parsed = Date.parse(String(value).replace(' ', 'T'));
  return Number.isFinite(parsed) ? String(parsed) : String(value);
}

/**
 * Pusher `error_record.updated` is `{ id, event, updated_at }` — not table-complete.
 * A same-client SESSION_SAFE_UPDATE overlay can consume its own echo when id+updated_at match.
 * An id-only payload cannot prove echo vs remote and must not be treated as safe.
 */
export function isAcknowledgedErrorRecordUpdateEcho(payload, overlay) {
  if (!payload || typeof payload !== 'object') return false;
  if (!overlay || typeof overlay.get !== 'function') return false;
  const id = payload.id;
  const updatedAt = payload.updated_at;
  if (id == null || id === '' || updatedAt == null || updatedAt === '') return false;
  const row = overlay.get(String(id));
  if (!row || typeof row !== 'object' || row.updated_at == null || row.updated_at === '') return false;
  return normalizeErrorRecordTimestamp(row.updated_at) === normalizeErrorRecordTimestamp(updatedAt);
}

export function emitErrorRecordListMutation(detail) {
  window.dispatchEvent(new CustomEvent(ERROR_RECORDS_UPDATED_EVENT, {
    detail: detail && typeof detail === 'object' ? detail : { operation: 'invalidate' },
  }));
}

export function appendErrorRecordWritePayload(formData, values, extras = {}) {
  const payload = values && typeof values === 'object' ? values : {};
  ERROR_RECORD_WRITE_FIELDS.forEach((key) => {
    const value = payload[key];
    formData.append(key, value == null ? '' : value);
  });
  if (Object.prototype.hasOwnProperty.call(extras, 'contractor_id')) {
    formData.append('contractor_id', extras.contractor_id == null ? '' : extras.contractor_id);
  }
}
